import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { Grid, List, ListItem, Box, Button, Menu, MenuItem, Chip, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { InspectionStatuses } from 'types';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddLinkIcon from '@mui/icons-material/AddLink';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import { menuLightGrey } from 'styles/vars';
import { GVTypography } from 'components/lib';
import { makeStyles } from 'tss-react/mui';
import { useUserStateStore } from 'zstore/userStateStore';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import SupervisorAccountTwoToneIcon from '@mui/icons-material/SupervisorAccountTwoTone';
import { SearchBar } from '@globalvisioninc/ui/compositions';
import InspectionCopyPrepModal from 'components/modals/InspectionCopyPrepModal';
import ReportDownloadMenu from 'components/common/GVDocumentsBar/ReportDownloadMenu';
import { createMultiInspectionReport, CreateMultiInspectionReportPayload } from 'store/report/requests';
import { ReportStates, useReportState } from 'zstore/reportStore';
import { app, report } from 'store';
import LinkIcon from '@mui/icons-material/Link';
import { useFlags } from 'launchdarkly-react-client-sdk';
import axiosInstanceQueries from 'store/queries/queriesAxios';
import {
  InspectionListItem,
  InspectionOwnership,
  useInspectionsInfinite,
} from 'store/queries/inspections/useInspectionsInfinite';
import { ApiLinkedInspection } from 'store/request/inspections/types';
import SessionListTabs, { SessionListTab } from './SessionListTabs';
import styles from './SessionList.module.css';

const useStyles = makeStyles()((theme) => {
  return {
    table: {
      flex: '1',
      margin: `var(--gds-spacing-lg) 0`,
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarGutter: 'stable',
      '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#393B3F',
        boxShadow: '0 0 1px 1px #474747',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '10px',
        background: 'rgba(100, 102, 105, 0.6)',
        boxShadow: '0 0 1px 1px #393B3F',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#4e4e4e',
      },
    },
    listItem: {
      background: menuLightGrey,
      borderRadius: '4px',
      margin: `var(--gds-spacing-xs) var(--gds-spacing-xl)`,
      padding: `var(--gds-spacing-xs) var(--gds-spacing-lg) var(--gds-spacing-xs) var(--gds-spacing-sm)`,
      width: '332px',
      height: '41px',
    },
    whiteHover: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    rightAlignment: {
      textAlign: 'right',
    },
    rowCheckbox: {
      '& .MuiFormControlLabel-root': {
        marginLeft: '-4px',
        marginRight: '8px',
      },
    },
    textList: {
      lineHeight: '24px',
      marginRight: 'var(--gds-spacing-md)',
    },
    buttonIcon: {
      minWidth: '24px',
      height: '24px',
    },
    truncatedText: {
      width: '170px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'inline-block',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: theme.spacing(4, 3),
    },
    compactEmptyStateImage: {
      width: '100px',
      height: '100px',
      marginTop: theme.spacing(3.25),
      marginBottom: theme.spacing(3.25),
    },
    noSharedInspectionImage: {
      width: '152px',
      height: '152px',
    },
    emptyStateTitle: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: 0,
    },
    emptyStateSubtitle: {
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '150%',
      letterSpacing: 0,
      width: '174px',
      color: 'rgba(255, 255, 255, 0.6)',
      marginTop: theme.spacing(1.5),
    },
  };
});

const getInspectionUrl = (id: string, status: InspectionStatuses, isShared: boolean) => {
  // If inspection is shared, always go to results
  if (isShared) {
    return `/inspection/${id}/result`;
  }
  // Otherwise use normal routing logic
  if (status === InspectionStatuses.completed) {
    return `/inspection/${id}/result`;
  }
  return `/inspection/${id}`;
};

const SEARCH_DEBOUNCE_MS = 300;

// Filter to show only first inspection from each group
const filterGroupedInspections = (allInspections: InspectionListItem[], preferWithResults = false) => {
  const seenGroups = new Set<string>();
  const groupFirstInspections = new Map<string, InspectionListItem>();

  // First pass: find the best representative for each group
  allInspections.forEach((inspection) => {
    const groupId = inspection.settings?.groupId;
    if (groupId) {
      const existing = groupFirstInspections.get(groupId);
      // If preferWithResults, prioritize inspections with lastJobId
      if (!existing || (preferWithResults && !existing.lastJobId && inspection.lastJobId)) {
        groupFirstInspections.set(groupId, inspection);
      }
    }
  });

  // Second pass: filter to keep only group representatives and non-grouped inspections
  return allInspections.filter((inspection) => {
    const groupId = inspection.settings?.groupId;
    if (groupId) {
      if (seenGroups.has(groupId)) {
        return false; // Already seen this group
      }
      // Check if this is the chosen representative for the group (compare by ID)
      const representative = groupFirstInspections.get(groupId);
      if (representative && representative.id === inspection.id) {
        seenGroups.add(groupId);
        // check if the inspection has a lastJobId
        if (preferWithResults && !inspection.lastJobId) {
          return false;
        }
        return true;
      }
      return false;
    }
    if (preferWithResults && !inspection.lastJobId) {
      return false;
    }
    return true; // Keep all inspections without groupId
  });
};

const MAX_CONSECUTIVE_AUTO_FETCHES = 3;

const SessionList = () => {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const sentinelObserverRef = useRef<IntersectionObserver | null>(null);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);
  const consecutiveAutoFetchesRef = useRef(0);
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [copyPrepModalOpen, setCopyPrepModalOpen] = useState<boolean>(false);
  const { showLinkInspections } = useFlags();
  const [selectedInspection, setSelectedInspection] = useState<{ id: string; name: string } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInspection, setMenuInspection] = useState<{
    id: string;
    name: string;
    status: InspectionStatuses;
    isShared: boolean;
  } | null>(null);
  const [multiSelectionMode, setMultiSelectionMode] = useState<boolean>(false);
  const [selectedInspections, setSelectedInspections] = useState<InspectionListItem[]>([]);
  const selectedInspectionIds = useMemo(() => selectedInspections.map((i) => i.id), [selectedInspections]);
  const [multiReportModalOpen, setMultiReportModalOpen] = useState<boolean>(false);
  const [multiReportInspectionIds, setMultiReportInspectionIds] = useState<string[]>([]);
  const [anyHaveSourceFile, setAnyHaveSourceFile] = useState<boolean>(false);
  const [generatedReportName, setGeneratedReportName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<SessionListTab>('my');
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth0();
  const { user } = useUserStateStore();
  const { reportState, setReportState, reportPath, setIsMultiInspection } = useReportState();

  const isSearchActive = debouncedSearch.trim().length > 0;
  const activeOwnership: InspectionOwnership = activeTab === 'my' ? 'owned' : 'shared';
  const queryOwnership: InspectionOwnership = isSearchActive ? 'all' : activeOwnership;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError } = useInspectionsInfinite({
    ownership: queryOwnership,
    search: debouncedSearch.trim(),
    enabled: isAuthenticated && !!user?.sub,
  });

  // Prefetch the opposite tab so switching tabs is instant. Also used to decide
  // whether the multi-report toggle can appear (selections persist across tabs).
  const { data: otherTabData } = useInspectionsInfinite({
    ownership: activeOwnership === 'owned' ? 'shared' : 'owned',
    enabled: isAuthenticated && !!user?.sub && !isSearchActive,
  });

  const inspections = useMemo<InspectionListItem[]>(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const otherTabInspectionCount = useMemo(
    () => otherTabData?.pages.flatMap((page) => page.items).length ?? 0,
    [otherTabData],
  );

  const visibleInspections = useMemo(() => {
    if (isSearchActive) return inspections;
    return filterGroupedInspections(inspections, multiSelectionMode);
  }, [inspections, isSearchActive, multiSelectionMode]);

  // Empty state for the Shared tab: only after a real fetch resolved with zero
  // results, never during initial load, errors, or when the search view is active.
  const showSharedEmptyState =
    activeTab === 'shared' && !isSearchActive && !isPending && !isError && inspections.length === 0;

  // Empty state for the My Inspections tab when the user has no inspections yet.
  const showOwnedEmptyState =
    activeTab === 'my' && !isSearchActive && !isPending && !isError && inspections.length === 0;

  // Empty state for an active search that returned no matches.
  const showSearchEmptyState = isSearchActive && !isPending && !isError && inspections.length === 0;

  const handleSearchChange = (value: string): void => {
    setSearchInput(value);
    if (value === '') {
      setDebouncedSearch('');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Warm the browser cache for empty-state assets so switching tabs doesn't
  // briefly flash a stale or unloaded image.
  useEffect(() => {
    [
      '/icons/empty-shared-inspections.svg',
      '/icons/empty-my-inspections.webp',
      '/icons/empty-search-inspections.webp',
    ].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [debouncedSearch, activeTab]);

  // Refs keep the observer callback stable so it isn't rebuilt on every fetch.
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);
  useEffect(() => {
    hasNextPageRef.current = hasNextPage;
    isFetchingNextPageRef.current = isFetchingNextPage;
    fetchNextPageRef.current = fetchNextPage;
  });

  const attachSentinel = useCallback((node: HTMLDivElement | null) => {
    sentinelNodeRef.current = node;
    if (sentinelObserverRef.current) {
      sentinelObserverRef.current.disconnect();
      sentinelObserverRef.current = null;
    }
    if (!node || !tableRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          consecutiveAutoFetchesRef.current = 0;
          return;
        }
        if (!hasNextPageRef.current || isFetchingNextPageRef.current) return;
        if (consecutiveAutoFetchesRef.current >= MAX_CONSECUTIVE_AUTO_FETCHES) return;
        consecutiveAutoFetchesRef.current += 1;
        fetchNextPageRef.current();
      },
      { root: tableRef.current, rootMargin: '200px', threshold: 0 },
    );
    observer.observe(node);
    sentinelObserverRef.current = observer;
  }, []);

  useEffect(() => () => sentinelObserverRef.current?.disconnect(), []);

  // When a page arrives that filters out to zero new visible rows, the sentinel
  // doesn't move and the observer stays silent. Re-observing re-fires the
  // callback with the current intersection state; the auto-fetch cap still applies.
  useEffect(() => {
    const observer = sentinelObserverRef.current;
    const node = sentinelNodeRef.current;
    if (!observer || !node) return;
    observer.unobserve(node);
    observer.observe(node);
  }, [inspections.length]);

  useEffect(() => {
    consecutiveAutoFetchesRef.current = 0;
  }, [activeTab, debouncedSearch, multiSelectionMode]);

  // Reset the budget whenever a fetch grows the visible list, so the cap only
  // fires on a true runaway (consecutive pages with zero new visible rows).
  const previousVisibleCountRef = useRef(0);
  useEffect(() => {
    if (visibleInspections.length > previousVisibleCountRef.current) {
      consecutiveAutoFetchesRef.current = 0;
    }
    previousVisibleCountRef.current = visibleInspections.length;
  }, [visibleInspections.length]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, session: InspectionListItem) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuInspection({
      id: session.id,
      name: session.name,
      status: session.status,
      isShared: session.isShared,
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuInspection(null);
  };

  const handleOpenCopyPrepModal = () => {
    if (menuInspection) {
      setSelectedInspection({ id: menuInspection.id, name: menuInspection.name });
      setCopyPrepModalOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseCopyPrepModal = () => {
    setCopyPrepModalOpen(false);
    setSelectedInspection(null);
  };

  const handleToggleMultiReportMode = () => {
    setMultiSelectionMode(!multiSelectionMode);
    setSelectedInspections([]); // Clear selections when toggling
  };

  const handleGenerateMultiReport = () => {
    const run = async () => {
      const groupIdsToFetch = new Set<string>();
      selectedInspections.forEach((insp) => {
        if (insp.settings?.groupId) {
          groupIdsToFetch.add(insp.settings.groupId);
        }
      });

      const linkedByGroup = new Map<string, ApiLinkedInspection[]>();
      if (groupIdsToFetch.size > 0) {
        const fetched = await Promise.all(
          Array.from(groupIdsToFetch).map((groupId) =>
            queryClient.fetchQuery<ApiLinkedInspection[]>({
              queryKey: ['inspections', 'linked', groupId],
              queryFn: async () => {
                const { data } = await axiosInstanceQueries.get<ApiLinkedInspection[]>(
                  `/inspections/linked/${groupId}`,
                );
                return data;
              },
              staleTime: 1000 * 30,
            }),
          ),
        );
        Array.from(groupIdsToFetch).forEach((groupId, idx) => {
          linkedByGroup.set(groupId, fetched[idx] ?? []);
        });
      }

      // Picks have full settings; siblings come from /inspections/linked which strips
      // settings to { groupId }, so treat "any sibling exists" as "may have a source file".
      const picksHaveSource = selectedInspections.some((insp) => !insp.settings?.singleFile);
      const anySiblings = Array.from(linkedByGroup.values()).some((siblings) => siblings.length > 0);
      setAnyHaveSourceFile(picksHaveSource || anySiblings);

      const expandedIds = new Set<string>();
      selectedInspections.forEach((insp) => {
        const groupId = insp.settings?.groupId;
        if (!groupId) {
          expandedIds.add(insp.id);
          return;
        }
        if (insp.lastJobId) expandedIds.add(insp.id);
        const siblings = linkedByGroup.get(groupId) ?? [];
        siblings.forEach((sibling) => {
          if (sibling.lastJobId) expandedIds.add(sibling.id);
        });
      });

      setMultiReportInspectionIds(Array.from(expandedIds));
      setMultiReportModalOpen(true);
    };

    run().catch(() => {
      dispatch(
        app.actions.setSnackMessage({
          message: 'There was an error preparing the report. Please try again.',
          type: 'error',
        }),
      );
    });
  };

  const handleCloseMultiReportModal = () => {
    setMultiReportModalOpen(false);
  };

  const handleGenerateReport = (payload: CreateMultiInspectionReportPayload, reportName: string) => {
    setGeneratedReportName(reportName);

    // Mark the flow as multi-inspection BEFORE dispatching so any error
    // handler (in store/report/requests.ts) sees the flag and resets state
    // to `Closed` instead of leaving `Idle`, which would otherwise auto-open
    // the single-mode report modal next time the user lands on a Results page.
    setIsMultiInspection(true);
    setReportState(ReportStates.Generating);

    dispatch(createMultiInspectionReport(payload));

    setMultiSelectionMode(false);
    setSelectedInspections([]);
    setMultiReportInspectionIds([]);
  };

  // Handle multi-inspection report completion
  useEffect(() => {
    if (reportState === ReportStates.Generated && generatedReportName) {
      dispatch(
        report.actions.downloadReport({
          reportName: generatedReportName,
          reportPath: reportPath,
        }),
      );
      setGeneratedReportName('');
      setIsMultiInspection(false);
      setReportState(ReportStates.Closed);
    }
  }, [reportState, generatedReportName, reportPath, dispatch]);

  const multiInspectionProp = useMemo(
    () => ({
      open: multiReportModalOpen,
      onClose: handleCloseMultiReportModal,
      inspectionIds: multiReportInspectionIds,
      onGenerate: handleGenerateReport,
      anyHaveSourceFile,
    }),
    [multiReportModalOpen, multiReportInspectionIds, anyHaveSourceFile],
  );

  const handleTabChange = (newTab: SessionListTab) => {
    setActiveTab(newTab);
    setSearchInput('');
    setDebouncedSearch('');
  };

  const getSessionsList = (sessions: InspectionListItem[]) => {
    return sessions.map((session) => {
      const url = getInspectionUrl(session.id, session.status, session.isShared);
      const date = new Date(session.updatedAt);
      const dateString = new Intl.DateTimeFormat('en-GB').format(date);

      const isSelected = selectedInspectionIds.includes(session.id);
      const linkedCount = session.linkedCount ?? 0;

      return (
        <ListItem
          disableGutters
          divider
          className={`${classes.listItem} ${classes.whiteHover}`}
          key={session.id}
          button
          component={multiSelectionMode ? 'div' : Link}
          to={multiSelectionMode ? undefined : url}
          onClick={
            multiSelectionMode
              ? () => {
                  setSelectedInspections((prev) =>
                    prev.some((i) => i.id === session.id)
                      ? prev.filter((i) => i.id !== session.id)
                      : [...prev, session],
                  );
                }
              : undefined
          }
          disableRipple={multiSelectionMode}
          sx={{ position: 'relative' }}
        >
          <Grid container alignItems="center">
            {multiSelectionMode && (
              <Grid item onClick={(e) => e.stopPropagation()} className={classes.rowCheckbox}>
                <GVCheckbox
                  checked={isSelected}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectedInspections((prev) =>
                      checked
                        ? prev.some((i) => i.id === session.id)
                          ? prev
                          : [...prev, session]
                        : prev.filter((i) => i.id !== session.id),
                    );
                  }}
                  aria-label={`Select ${session.name}`}
                />
              </Grid>
            )}
            <Grid item xs={multiSelectionMode ? 6.5 : 7.5}>
              <GVTypography
                variant="body1"
                className={classes.textList}
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GVTooltip title={session.name} side="bottom">
                  <span className={classes.truncatedText} style={{ width: '170px' }}>
                    {session.name}
                  </span>
                </GVTooltip>
                {linkedCount > 1 && (
                  <GVTooltip title={`${linkedCount} linked inspections`} side="top">
                    <Chip
                      icon={<LinkIcon sx={{ fontSize: '12px !important' }} />}
                      label={linkedCount}
                      id={`${session.name}-linked-inspections`}
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '11px',
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                        color: '#4caf50',
                        marginLeft: '8px',
                        '& .MuiChip-icon': {
                          fontSize: '12px',
                          color: '#4caf50',
                        },
                      }}
                    />
                  </GVTooltip>
                )}
                {session.isShared && (
                  <GVTooltip title="Shared inspection" side="top">
                    <SupervisorAccountTwoToneIcon
                      sx={{
                        marginLeft: '8px',
                        fontSize: '1rem',
                        color: 'var(--color-secondary)',
                      }}
                    />
                  </GVTooltip>
                )}
              </GVTypography>
            </Grid>
            <Grid item xs={3.5} className={classes.rightAlignment}>
              <GVTypography variant="body1" className={classes.textList}>
                {dateString}
              </GVTypography>
            </Grid>
            {!multiSelectionMode && showLinkInspections && (
              <Grid item xs={1} className={classes.rightAlignment}>
                <Button
                  className={`${classes.whiteHover} ${classes.buttonIcon}`}
                  onClick={(e) => handleMenuOpen(e, session)}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="Session actions"
                  disableRipple
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            )}
          </Grid>
        </ListItem>
      );
    });
  };

  return (
    <>
      <SearchBar
        placeholder="Search by inspection name"
        value={searchInput}
        onChange={handleSearchChange}
        className={styles.search}
      />

      <SessionListTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isSearchActive={isSearchActive}
        inspectionCount={inspections.length}
        otherTabInspectionCount={otherTabInspectionCount}
        multiSelectionMode={multiSelectionMode}
        onToggleMultiSelectionMode={handleToggleMultiReportMode}
        selectedInspectionCount={selectedInspectionIds.length}
        onGenerateMultiReport={handleGenerateMultiReport}
      />

      <Grid
        item
        container
        direction="column"
        wrap="nowrap"
        className={classes.table}
        ref={tableRef}
        data-testid="session-list-scroll"
      >
        {isPending ? (
          <Box className={classes.emptyState}>
            <CircularProgress
              size={64}
              thickness={4}
              color="secondary"
              sx={{ mt: 10 }}
              data-testid="inspections-list-loading"
            />
          </Box>
        ) : showSearchEmptyState ? (
          <Box key="empty-search" className={classes.emptyState} data-testid="search-empty-state">
            <img
              src="/icons/empty-search-inspections.webp"
              alt=""
              width={100}
              height={100}
              className={classes.compactEmptyStateImage}
            />
            <GVTypography className={classes.emptyStateTitle}>No inspections found</GVTypography>
            <GVTypography className={classes.emptyStateSubtitle}>Try a different search term</GVTypography>
          </Box>
        ) : showSharedEmptyState ? (
          <Box key="empty-shared" className={classes.emptyState} data-testid="shared-empty-state">
            <img
              src="/icons/empty-shared-inspections.svg"
              alt=""
              width={152}
              height={152}
              className={classes.noSharedInspectionImage}
            />
            <GVTypography className={classes.emptyStateTitle}>No shared inspections found</GVTypography>
            <GVTypography className={classes.emptyStateSubtitle}>
              Inspections will appear here once they are shared
            </GVTypography>
          </Box>
        ) : showOwnedEmptyState ? (
          <Box key="empty-owned" className={classes.emptyState} data-testid="owned-empty-state">
            <img
              src="/icons/empty-my-inspections.webp"
              alt=""
              width={100}
              height={100}
              className={classes.compactEmptyStateImage}
            />
            <GVTypography className={classes.emptyStateTitle}>Let&apos;s get inspecting</GVTypography>
            <GVTypography className={classes.emptyStateSubtitle}>Upload files to start a new inspection</GVTypography>
          </Box>
        ) : (
          <List dense>
            <div id="session-list">
              {getSessionsList(visibleInspections)}
              {hasNextPage && <div ref={attachSentinel} aria-hidden="true" style={{ height: 1 }} />}
            </div>
          </List>
        )}
      </Grid>

      {/* Session Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleOpenCopyPrepModal}>
          <AddLinkIcon fontSize="small" sx={{ marginRight: 1 }} />
          Create Linked Inspection
        </MenuItem>
      </Menu>

      {/* Create Linked Inspection Modal */}
      {selectedInspection && (
        <InspectionCopyPrepModal
          open={copyPrepModalOpen}
          onClose={handleCloseCopyPrepModal}
          inspectionId={selectedInspection.id}
          inspectionName={selectedInspection.name}
        />
      )}

      {/* Multi-Inspection Report Modal (shared with single-inspection ReportDownloadMenu) */}
      <ReportDownloadMenu multiInspection={multiInspectionProp} />
    </>
  );
};

export default SessionList;
